import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { LineChart } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import api from "@/lib/api";

/**
 * Daily portfolio-value time series for the given scope. Backfilled from the
 * `holdings_snapshots` collection which is written every time /summary runs
 * (idempotent per UTC-day). Chart is a soft green area chart matching the DPP
 * palette. Range picker toggles between 30/90/365 days.
 */
const RANGES = [
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
  { label: "1Y",  days: 365 },
];

export default function PortfolioValueChart({ portfolioId }) {
  const [days, setDays] = useState(90);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = { days };
        if (portfolioId) params.portfolio_id = portfolioId;
        const r = await api.get("/holdings/history", { params });
        if (!cancelled) setPoints(r.data.points || []);
      } catch {
        if (!cancelled) setPoints([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, portfolioId]);

  // Growth = latest vs earliest point in the visible window.
  const first = points[0];
  const last = points[points.length - 1];
  const changeAbs = first && last ? last.total_value - first.total_value : 0;
  const changePct = first?.total_value ? (changeAbs / first.total_value) * 100 : 0;

  return (
    <Card data-testid="portfolio-value-chart">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <LineChart className="w-4 h-4 text-primary" />
            <h3 className="serif text-xl">Portfolio value over time</h3>
          </div>
          <div className="inline-flex gap-1 rounded-full border border-border p-0.5" role="tablist">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                data-testid={`chart-range-${r.days}`}
                className={`px-2.5 py-0.5 rounded-full mono text-[10px] uppercase tracking-widest transition-colors ${
                  days === r.days ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {points.length >= 2 && (
          <div className="mb-2 flex items-baseline gap-2 flex-wrap">
            <div className="serif text-2xl">${Math.round(last.total_value).toLocaleString()}</div>
            <div className={`mono text-xs ${changeAbs >= 0 ? "text-primary" : "text-destructive"}`}>
              {changeAbs >= 0 ? "+" : ""}${Math.round(changeAbs).toLocaleString()} ({changeAbs >= 0 ? "+" : ""}{changePct.toFixed(2)}%)
            </div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground ml-auto">
              {points.length} day{points.length === 1 ? "" : "s"} tracked
            </div>
          </div>
        )}

        {loading && (
          <div className="h-40 grid place-items-center text-sm text-muted-foreground">Loading…</div>
        )}

        {!loading && points.length < 2 && (
          <div className="h-40 grid place-items-center text-center px-6">
            <div className="text-sm text-muted-foreground max-w-xs">
              DPP is starting to record daily snapshots of your portfolio value — the growth chart lights up after the second day. Come back tomorrow.
            </div>
          </div>
        )}

        {!loading && points.length >= 2 && (
          <div className="h-40 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={points} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => v.slice(5)}
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false} tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => `$${Math.round(v / 1000)}k`}
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false} tickLine={false}
                  domain={["auto", "auto"]}
                  width={40}
                />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                  formatter={(v) => [`$${Number(v).toLocaleString()}`, "Value"]}
                  labelFormatter={(v) => v}
                />
                <Area
                  type="monotone"
                  dataKey="total_value"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  fill="url(#valueGradient)"
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
